import { type ReportData, ReportingPeriod, type ReportSelectorData } from "@qendo/profile/reports";
import { toDate } from "date-fns";
import { router } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import { ActivityIndicator, Pressable, StyleSheet, View } from "react-native";
import { LineChart } from "react-native-gifted-charts";
import { ReportAverage } from "~/components/layout/reports/ReportAverage";
import { Title } from "~/components/layout/text";
import { Radius, Space } from "~/constants/sizing";
import { useReport } from "~/providers/ReportProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { filled, nullish } from "~/util";
import { formatDateString } from "~/util/dates";
import { setHeightOnLayout } from "~/util/layout";
import { extractLineSegments, getMaxYAxis, getMinYAxis } from "~/util/qendo/reports";
import { parseNumber } from "~/util/string";

export type MiniReportProps = {
    readonly report: ReportSelectorData;
};

export const MINI_REPORT_WIDTH = 160;
export const MINI_REPORT_HEIGHT = 200;

export const MiniReport = ({ report }: MiniReportProps) => {
    const { colours, background } = useTheme();
    const { getReport } = useReport();
    const [data, setData] = useState<ReportData | null>(null);
    const [height, setHeight] = useState(40);
    const onLayout = setHeightOnLayout(setHeight);
    const graph_height = useMemo(() => MINI_REPORT_HEIGHT - height, [height]);

    const from = formatDateString(new Date());

    // biome-ignore lint/correctness/useExhaustiveDependencies(getReport): API
    useEffect(() => {
        getReport({
            period: ReportingPeriod.last_week,
            trackable_id: report.trackable_id,
            from,
        }).then(setData);
    }, [from, report]);

    // biome-ignore lint/correctness/useExhaustiveDependencies(report.scale): readonly
    const { min, max, values, lineSegments } = useMemo(
        () => ({
            min: getMinYAxis(report.scale, data?.points),
            max: getMaxYAxis(report.scale, data?.points),
            values: data?.points.map(({ date, average }) => ({
                date: toDate(date),
                value: parseNumber(average),
                hideDataPoint: nullish(average),
            })),
            lineSegments: extractLineSegments(data?.points ?? []),
        }),
        [data?.points],
    );

    const onPress = () => {
        router.navigate({ pathname: "/more/reports/[id]/show", params: { id: report.id } });
    };

    return (
        <Pressable style={[styles.container, background.primary]} onPress={onPress}>
            <View style={styles.header} onLayout={onLayout}>
                <Title size="md">{report.title}</Title>
                <View style={styles.average}>
                    <ReportAverage
                        scale={report.scale}
                        scale_name={report.scale_name}
                        average={data?.average ?? report.average}
                        averageProps={{ weight: "semibold", size: "xl" }}
                        subtitleProps={{ size: "md" }}
                    />
                </View>
            </View>
            {filled(values) ? (
                <LineChart
                    data={values}
                    color={colours.reports[0]}
                    dataPointsColor={colours.reports[0]}
                    parentWidth={MINI_REPORT_WIDTH}
                    height={graph_height}
                    adjustToWidth
                    thickness={3}
                    dataPointsRadius={1.5}
                    hideYAxisText
                    xAxisLength={MINI_REPORT_WIDTH}
                    showXAxisIndices
                    initialSpacing={2}
                    endSpacing={2}
                    yAxisThickness={0}
                    xAxisThickness={1}
                    xAxisColor={colours.border.primary}
                    rulesType="solid"
                    noOfSections={7}
                    showVerticalLines
                    noOfVerticalLines={7}
                    verticalLinesColor={colours.border.primary}
                    disableScroll
                    maxValue={max}
                    yAxisOffset={min}
                    lineSegments={lineSegments}
                />
            ) : (
                <View style={styles.loading}>
                    <ActivityIndicator size="large" color={colours.brand} />
                </View>
            )}
        </Pressable>
    );
};

const styles = StyleSheet.create({
    container: {
        width: MINI_REPORT_WIDTH,
        height: MINI_REPORT_HEIGHT,
        overflow: "hidden",
        borderRadius: Radius.md,
    },
    header: {
        padding: Space.sm,
        paddingBottom: Space.md,
        gap: Space.xs,
        alignItems: "flex-start",
    },
    average: {
        gap: Space.xxs,
        flexDirection: "row",
        alignItems: "flex-end",
        justifyContent: "flex-start",
    },
    loading: {
        flex: 1,
        width: MINI_REPORT_WIDTH,
        alignItems: "center",
        justifyContent: "center",
    },
    graph: {
        flex: 1,
        width: MINI_REPORT_WIDTH,
    },
});
